import { useEffect, useState, createContext, useContext, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider } from "@/contexts/LanguageContext";

// Pages
import HomePage from "@/pages/HomePage";
import JoinPage from "@/pages/JoinPage";
import NewsDetailPage from "@/pages/NewsDetailPage";
import FAQPage from "@/pages/FAQPage";
import TryoutsPage from "@/pages/TryoutsPage";
import OfferResponsePage from "@/pages/OfferResponsePage";
import CoachesPage from "@/pages/CoachesPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsConditions from "@/pages/TermsConditions";
import ReeplayerPage from "@/pages/ReeplayerPage";
import RSVPPage from "@/pages/RSVPPage";
import EventsPage from "@/pages/EventsPage";
import SponsorsShowcasePage from "@/pages/SponsorsShowcasePage";
import TeamMapPage from "@/pages/TeamMapPage";
import ReferralPage from "@/pages/ReferralPage";
import HighlightsPage from "@/pages/HighlightsPage";
import TeamSchedulesPage from "@/pages/TeamSchedulesPage";
import ParentsPage from "@/pages/ParentsPage";

import { SiteContentCacheProvider } from "@/contexts/SiteContentCache";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check
    // AuthCallback will exchange the session_id and establish the session first
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(
            `${API}/auth/session`,
            { session_id: sessionId },
            { withCredentials: true }
          );
          setUser(response.data);
          navigate("/admin", { replace: true, state: { user: response.data } });
        } catch (error) {
          console.error("Auth session exchange failed:", error.response?.status, error.response?.data);
          if (error.response?.status === 403) {
            setError(error.response.data?.detail || "Access denied. Your email is not authorized.");
          } else {
            setError(`Login failed (${error.response?.status || 'network error'}). Please try again. If this keeps happening, contact support.`);
          }
        }
      } else {
        navigate("/admin/login", { replace: true });
      }
    };

    processAuth();
  }, [location, navigate, setUser]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0C2E3C] flex items-center justify-center p-4">
        <div className="bg-white rounded-sm p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z" /></svg>
          </div>
          <h2 className="font-heading text-2xl text-[#0C2E3C] mb-2">LOGIN FAILED</h2>
          <p className="text-[#6B8A8F] text-base mb-6" data-testid="access-denied-msg">{error}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => { window.location.href = "/admin/login"; }}
              className="bg-[#0C2E3C] text-white font-bold uppercase tracking-wider text-sm px-8 py-3 rounded-none hover:bg-[#0C2E3C]/90"
              data-testid="try-again-btn"
            >
              Try Again
            </button>
            <button
              onClick={() => { window.location.href = "/"; }}
              className="border-2 border-[#0C2E3C] text-[#0C2E3C] font-bold uppercase tracking-wider text-sm px-8 py-3 rounded-none hover:bg-[#0C2E3C]/10"
              data-testid="access-denied-home-btn"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0C2E3C] flex items-center justify-center">
      <div className="text-[#F0F4F3] text-xl">Authenticating...</div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user && !location.state?.user) {
      navigate("/admin/login", { replace: true });
    }
  }, [user, loading, navigate, location.state]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5DC] flex items-center justify-center">
        <div className="text-[#1B3D2F] text-xl font-heading">Loading...</div>
      </div>
    );
  }

  if (!user && !location.state?.user) {
    return null;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/join" element={<JoinPage />} />
      <Route path="/tryouts" element={<TryoutsPage />} />
      <Route path="/coaches" element={<CoachesPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/news/:postId" element={<NewsDetailPage />} />
      <Route path="/offer/:token/:action" element={<OfferResponsePage />} />
      <Route path="/rsvp/:token" element={<RSVPPage />} />
      <Route path="/events" element={<EventsPage />} />
      <Route path="/team-schedules" element={<TeamSchedulesPage />} />
      <Route path="/t/:slug" element={<TeamSchedulesPage />} />
      <Route path="/parents" element={<ParentsPage />} />
      <Route path="/sponsors" element={<SponsorsShowcasePage />} />
      <Route path="/map" element={<TeamMapPage />} />
      <Route path="/referral" element={<ReferralPage />} />
      <Route path="/highlights" element={<HighlightsPage />} />
      <Route path="/highlights/player/:playerName" element={<HighlightsPage />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsConditions />} />
      <Route path="/livestream" element={<ReeplayerPage />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <SiteContentCacheProvider>
          <BrowserRouter>
            <AuthProvider>
              <AppRouter />
              <Toaster position="top-right" />
            </AuthProvider>
          </BrowserRouter>
        </SiteContentCacheProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
export { API };
